import {FormFieldT, FormStepT} from '@/lib/types/form.types';
import {retailerUpdateT} from '@/lib/types/retailer.types';

import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

/****************************************************************************
 * Form Fields
 ****************************************************************************/
/*
 * Retailer Registration Form Fields
 */
export const retailerFormFields: FormFieldT<retailerUpdateT>[] = [
  {
    label: 'Retailer Name (Legal/Entity Name)',
    name: 'retailer_name',
    options: {required: 'Retailer name is required'},
  },
  {
    label: 'Same As Speculator Email',
    name: 'retailer_same_as_speculator_email',
    custom: true,
    type: 'checkbox',
    watch: {
      enabled: true,
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      configs: [
        {
          source: 'speculator_email',
          target: 'retailer_email',
          autoCopy: true,
        },
      ],
    },
  },
  {
    label: 'Retailer Email',
    name: 'retailer_email',
    type: 'email',
    options: {required: 'Retailer email is required'},
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_email',
        is: true,
      },
    },
  },
  {
    label: 'Phone Number',
    name: 'retailer_phone',
    type: 'tel',
    options: {
      required: 'Phone number is required',
      pattern: {
        value: /^\d{10}$/,
        message: 'Please enter a valid 10-digit phone number',
      },
    },
  },
  {
    label: 'License Number',
    name: 'retailer_license_number',
    options: {required: 'License number is required'},
  },
  {
    label: 'Geo Points',
    name: 'retailer_geo_points',
    type: 'text',
    options: {
      required: 'Geo points are required',
      pattern: {
        value: /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/,
        message: 'Please enter valid geo coordinates (latitude,longitude)',
      },
    },
  },
  {
    type: 'checkbox',
    custom: true,
    label: 'Same as Speculator Address',
    name: 'retailer_same_as_speculator_address',
    defaultValue: true,
    watch: {
      enabled: true,
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      configs: [
        {
          source: 'speculator_address',
          target: 'retailer_address',
        },
        {
          source: 'speculator_address2',
          target: 'retailer_address2',
        },
        {
          source: 'speculator_city',
          target: 'retailer_city',
        },
        {
          source: 'speculator_state',
          target: 'retailer_state',
        },
        {
          source: 'speculator_postal_code',
          target: 'retailer_postal_code',
        },
      ],
    },
  },
  {
    label: 'Address 1',
    name: 'retailer_address',
    options: {required: 'Address is required'},
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Address 2',
    name: 'retailer_address2',
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'City',
    name: 'retailer_city',
    options: {required: 'City is required'},
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'State',
    type: 'select',
    name: 'retailer_state',
    placeholder: 'Select State',
    selectOptions: US_STATES_DATA,
    options: {
      required: 'State is required',
      validate: value => !!US_STATES_DATA.find(state => state.value === value),
    },
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Postal Code',
    name: 'retailer_postal_code',
    options: {
      required: 'Postal code is required',
      pattern: {value: /^\d{5}$/, message: 'Invalid postal code'},
    },
    watchConfig: {
      disableWhen: {
        field: 'retailer_same_as_speculator_address',
        is: true,
      },
    },
  },
];

export type RetailerFieldNamesT = (typeof retailerFormFields)[number]['name'];

/****************************************************************************
 * Form Steps
 ****************************************************************************/
export const retailerFormSteps: FormStepT<RetailerFieldNamesT> = [
  {
    step: 0,
    name: 'personal',
    label: 'Retailer Information',
    fields: [
      'retailer_name',
      'retailer_same_as_speculator_email',
      'retailer_email',
      'retailer_phone',
      'retailer_license_number',
      'retailer_geo_points',
    ],
  },
  {
    step: 1,
    name: 'address',
    label: 'Retailer Address',
    fields: [
      'retailer_same_as_speculator_address',
      'retailer_address',
      'retailer_address2',
      'retailer_city',
      'retailer_state',
      'retailer_postal_code',
    ],
  },
];
